/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Please do not add any new "any" types here without consulting team lead.
 */
import { groupBy, filterBy } from "@progress/kendo-data-query";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import {
  ComboBox as KendoComboBox,
  ComboBoxFilterChangeEvent,
  DropDownList,
  DropDownListFilterChangeEvent,
  MultiSelect,
  MultiSelectFilterChangeEvent,
  ListItemProps,
} from "@progress/kendo-react-dropdowns";
import { FieldWrapper } from "@progress/kendo-react-form";
import {
  Input,
  RadioGroup as KendoRadioGroup,
  RadioGroupChangeEvent,
  TextArea,
  TextAreaChangeEvent,
} from "@progress/kendo-react-inputs";
import { Error, Label, Hint } from "@progress/kendo-react-labels";
import { ValidationErrors } from "final-form";
import compact from "lodash-es/compact";
import isArray from "lodash-es/isArray";
import isEqual from "lodash-es/isEqual";
import isString from "lodash-es/isString";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Field, FieldMetaState, FieldProps, useField } from "react-final-form";
import { useTranslation } from "react-i18next";
import useDeepCompareEffect, {
  useDeepCompareMemoize,
} from "use-deep-compare-effect";
import * as Yup from "yup";
import ListNoData from "./ListNoData";
import { FormSelectItemRender } from "./index";

export type ShowErrorFunc = (props: ShowErrorProps) => boolean;
export interface ShowErrorProps {
  meta: FieldMetaState<any>;
}

export const TYPE_PASSWORD = "password";
export const TYPE_TEXT = "text";
export const TYPE_TEXT_AREA = "textarea";
export const TYPE_EMAIL = "email";
export const TYPE_NUMBER = "number";
export const TYPE_URL = "url";
export const TYPE_TELEPHONE = "tel";
export const TYPE_DATE = "date";
export const TYPE_DATETIME_LOCAL = "datetime-local";
export const TYPE_MONTH = "month";
export const TYPE_TIME = "time";
export const TYPE_WEEK = "week";

export type TEXT_FIELD_TYPE =
  | typeof TYPE_PASSWORD
  | typeof TYPE_TEXT
  | typeof TYPE_EMAIL
  | typeof TYPE_NUMBER
  | typeof TYPE_URL
  | typeof TYPE_TELEPHONE
  | typeof TYPE_DATE
  | typeof TYPE_DATETIME_LOCAL
  | typeof TYPE_MONTH
  | typeof TYPE_TIME
  | typeof TYPE_WEEK;

export interface SelectData {
  name: string | undefined;
  id?: string | number | string[] | undefined;
  disabled?: boolean;
  checkAllDisabled?: boolean;
  custom?: string;
  groupField?: string;
  title?: string;
  /** Data-source type; drives the chip/list icon via `getImage`. */
  type?: string;
}

export type TextFieldProps = Partial<FieldProps<any, any>> & {
  name: string;
  type?: TEXT_FIELD_TYPE;
  required?: boolean;
  optional?: boolean;
};

export type TextAreaFieldProps = Partial<FieldProps<any, any>> & {
  name: string;
  required?: boolean;
  optional?: boolean;
};

export type DatePickerProps = Partial<FieldProps<any, any>> & {
  name: string;
  required?: boolean;
  optional?: boolean;
};

export type SelectProps = Partial<FieldProps<any, any>> & {
  name: string;
  required?: boolean;
  optional?: boolean;
  data?: SelectData[];
  getOptionValue?: (option: any) => any;
  getOptionLabel?: (option: any) => any;
};

export interface ComboBoxProps extends SelectProps {
  newLabel?: string;
  newGroupField?: string;
  customItems?: { prependText: string; groupField?: string }[];
  customItemsLabel?: string;
}

export const showErrorOnChange: ShowErrorFunc = ({
  meta: { submitError, dirtySinceLastSubmit, error, touched, modified },
}: ShowErrorProps) =>
  !!(
    ((submitError && !dirtySinceLastSubmit) || error) &&
    (touched || modified)
  );

const config = {
  subscription: {
    error: true,
    submitError: true,
    dirtySinceLastSubmit: true,
    touched: true,
    modified: true,
  },
};

export const useFieldForErrors = (name: string) => useField(name, config);

export const FinalFormWrapper: React.FC<
  Partial<FieldProps<any, any>>
> = props => {
  const { input, meta, label, className, optional, children, ...rest } = props;
  const { helperText, error, hint } = rest;
  const inputId = input.id || input.name;
  const showValidationMessage = (meta?.error && meta?.touched) || error;
  const errorId = showValidationMessage ? `${inputId}_error` : "";
  const { t } = useTranslation();
  const isError =
    showErrorOnChange({ meta }) || error || error?.length || props.error;
  const errorMessage = error || meta.error || meta.submitError || props.error;
  return (
    <div className="tlx-form-field">
      <FieldWrapper className={className}>
        <Label editorId={inputId} editorDisabled={props.disabled}>
          {label}
          {optional && label && <i> {t("common.optional")}</i>}
        </Label>
        {children}
      </FieldWrapper>
      {hint && <Hint id={`${inputId}_hint`}>{hint}</Hint>}
      {isError && <Error id={errorId}>{errorMessage}</Error>}
      {helperText && <Error id={errorId}>{helperText}</Error>}
    </div>
  );
};

export const RadioGroup = (options: Partial<FieldProps<any, any>>) => {
  const { input, meta, ...rest } = options;
  const data: any = isArray(meta?.data) ? meta?.data : [];
  return (
    <FinalFormWrapper {...options}>
      <KendoRadioGroup
        {...input}
        {...rest}
        data={data}
        onChange={(event: RadioGroupChangeEvent) => input.onChange(event.value)}
      />
    </FinalFormWrapper>
  );
};

export function TextField(props: TextFieldProps) {
  const { name, disabled, className, type, ...rest } = props;

  const inputReference = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.autoFocus) {
      inputReference.current?.focus();
    }
  }, [props.autoFocus]);

  return (
    <Field
      name={name}
      type={type}
      render={({ input, meta }) => (
        <FinalFormWrapper
          input={input}
          meta={meta}
          disabled={disabled}
          className={className}
          {...rest}
        >
          <Input
            disabled={disabled}
            {...input}
            {...rest}
            ref={inputReference}
            valid={!showErrorOnChange({ meta })}
            type={type}
            label=""
          />
        </FinalFormWrapper>
      )}
      {...rest}
    />
  );
}

export function TextAreaField(props: TextAreaFieldProps) {
  const { name, disabled, className, ...rest } = props;

  return (
    <Field
      name={name}
      render={({ input, meta }) => {
        const { onChange } = input;
        input.onChange = (event: TextAreaChangeEvent) => {
          onChange(event.value);
        };
        return (
          <FinalFormWrapper
            input={input}
            meta={meta}
            disabled={disabled}
            className={className}
            {...rest}
          >
            <TextArea
              disabled={disabled}
              {...input}
              {...rest}
              valid={!showErrorOnChange({ meta })}
            />
          </FinalFormWrapper>
        );
      }}
      {...rest}
    />
  );
}

const SelectComponent = (props: SelectProps) => {
  const {
    input,
    name,
    data = [],
    meta,
    disabled,
    isLoading,
    defaultItem = { name: "Please choose an option" },
    getOptionLabel = item => item.name,
    getOptionValue = item => item.id,
    itemRender,
    valueRender,
    ...rest
  } = props;
  const [value, setValue] = React.useState(
    isString(input.value) && input.value.length > 0
      ? { id: input.value, name: input.value }
      : input.value,
  );

  useEffect(() => {
    // for reset value we need to setValue as empty
    if (!input.value) {
      setValue(undefined);
    }
  }, [input.value]);

  const [dropDownData, setDropDownData] = React.useState<SelectData[]>([]);
  const newData: SelectData[] = useMemo(() => {
    return data.map((item: any) => ({
      ...((typeof item === "object" && item) || {}),
      name: getOptionLabel(item),
      id: getOptionValue(item),
      optionInfoText: item?.optionInfoText,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDeepCompareMemoize(data)]);

  useDeepCompareEffect(() => {
    if (value && value.name === value.id) {
      const newValue = data.find(item => item.id === value.id);
      if (newValue) setValue(newValue);
    }
  }, [data]);

  useDeepCompareEffect(() => {
    setDropDownData(newData);
  }, [newData]);

  const filterChange = (event: DropDownListFilterChangeEvent) => {
    setDropDownData(filterBy([...newData], event.filter));
  };

  const placeHolder = input.value
    ? undefined
    : isLoading
      ? { name: "Loading..." }
      : defaultItem;

  const isError = showErrorOnChange({ meta });

  return (
    <FinalFormWrapper input={input} meta={meta} {...rest}>
      <DropDownList
        value={value || undefined}
        style={rest.style}
        textField="name"
        name={name}
        key={input.value}
        dataItemKey="id"
        disabled={disabled}
        loading={isLoading}
        valid={!isError}
        defaultItem={placeHolder}
        filterable={newData.length > 5}
        onFilterChange={filterChange}
        onChange={event => {
          const { name, id } = event.value;
          setValue(event.value);
          input.onChange(id || name);
          rest.onChange?.(id || name);
        }}
        data={dropDownData}
        listNoDataRender={() => <ListNoData message={rest.noDataMessage} />}
        itemRender={itemRender || FormSelectItemRender}
        valueRender={valueRender}
      />
    </FinalFormWrapper>
  );
};

export const itemComboBoxRender = (
  li: React.ReactElement<HTMLLIElement>,
  itemProps: ListItemProps,
) => {
  const { dataItem } = itemProps;
  const liProps = { ...li.props };
  if (dataItem.title) {
    liProps.title = dataItem.title;
  }
  const itemChildren = (
    <span>
      {li.props.children}{" "}
      {dataItem.custom && <strong>({dataItem.custom})</strong>}
    </span>
  );

  return React.cloneElement(li, liProps, itemChildren);
};

const ComboBoxComponent = (props: ComboBoxProps) => {
  const {
    input,
    name,
    data = [],
    meta,
    disabled,
    isLoading,
    placeholder,
    getOptionLabel = item => item.name,
    getOptionValue = item => item.id,
    newLabel = "Custom",
    newGroupField,
    customItems = [],
    customItemsLabel = "Custom",
    groupField,
    ...rest
  } = props;
  const [value, setValue] = React.useState(
    isString(input.value) && input.value.length > 0
      ? { id: input.value, name: input.value }
      : input.value,
  );

  useEffect(() => {
    // for reset value we need to setValue as empty
    if (!input.value) {
      setValue(undefined);
    }
  }, [input.value]);

  const [dropDownData, setDropDownData] = React.useState<SelectData[]>([]);
  const newData: SelectData[] = useMemo(() => {
    return data.map((item: any) => ({
      name: getOptionLabel(item),
      id: getOptionValue(item),
      groupField: item.groupField,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDeepCompareMemoize(data)]);

  useDeepCompareEffect(() => {
    if (value && value.name === value.id) {
      const newValue = data.find(item => item.id === value.id);
      if (newValue) setValue(newValue);
    }
  }, [data]);

  useDeepCompareEffect(() => {
    setDropDownData(newData);
  }, [newData]);

  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    const { filter } = event;
    const items = filterBy([...newData], filter);
    if (
      filter.value.length > 2 &&
      !items.find(item => item.name === filter.value)
    ) {
      customItems.forEach(item => {
        // adding extra custom option with prependText if coming from props
        const id = `${item.prependText || ""}${filter.value}`;
        items.unshift({
          id,
          name: id,
          custom: customItemsLabel,
          groupField: item.groupField,
        });
      });

      items.unshift({
        id: filter.value,
        name: `${filter.value}`,
        custom: newLabel,
        groupField: newGroupField,
      });
    }

    setDropDownData(() =>
      groupField
        ? (groupBy(items, [{ field: groupField }]) as any[]).reduce(
            (res, group) => [...res, ...group.items],
            [],
          )
        : items,
    );
  };

  const placeHolder = input.value
    ? undefined
    : isLoading
      ? "Loading"
      : placeholder;

  const isError = showErrorOnChange({ meta });
  return (
    <FinalFormWrapper input={input} meta={meta} {...rest}>
      <KendoComboBox
        value={value || undefined}
        textField="name"
        name={name}
        key={input.value}
        dataItemKey="id"
        disabled={disabled}
        loading={isLoading}
        valid={!isError}
        filterable={newData.length > 5}
        placeholder={placeHolder}
        allowCustom={true}
        onFilterChange={filterChange}
        itemRender={itemComboBoxRender}
        onChange={event => {
          const { name, id } = event.value || { name: "" };
          setValue(event.value);
          input.onChange(id || name);
        }}
        data={dropDownData}
        groupField={groupField}
      />
    </FinalFormWrapper>
  );
};

const FormMultiSelect = (props: SelectProps) => {
  const {
    input,
    name,
    disabled,
    options: data = [],
    placeholder = "Please choose an option",
    isLoading,
    meta,
    getOptionLabel = item => item.name,
    getOptionValue = item => item.id,
    ...rest
  } = props;
  const [dropDownData, setDropDownData] = React.useState<SelectData[]>([]);
  const [value, setValue] = React.useState<SelectData[]>(
    isString(input.value) && input.value.length
      ? [{ id: input.value, name: input.value }]
      : isArray(input.value)
        ? input.value.map((item: string) => ({ id: item, name: item }))
        : [],
  );

  const hasAll =
    value?.find(
      (item: SelectData) => (item.name as string)?.toLowerCase() === "all",
    )?.name !== undefined;

  useEffect(() => {
    // for reset value we need to setValue as empty
    if (!input.value) {
      setValue([]);
    }
  }, [input.value]);

  const newData: SelectData[] = useMemo(() => {
    // RegExp to split string at the last dot (e.g., "schema.table" → ["schema", "table"])
    const regExp = /\.(?=[^.]+$)/;

    // Special suffixes that represent "select all" options
    const possibleAll = [
      "ALL_SCHEMAS",
      "ALL_FUNCTIONS",
      "ALL_VIEWS",
      "ALL_TABLES",
      "ALL_PROCEDURES",
    ];

    return data.map((item: any) => {
      // Find if there's already a selected item with the same prefix (before the last dot)
      const getObject = value?.find(
        (valItem: SelectData) =>
          valItem?.name?.split(regExp)?.[0] === item.name.split(regExp)[0],
      );

      return {
        name: getOptionLabel(item),
        id: getOptionValue(item),
        disabled: (() => {
          // If "All" is selected, disable everything except the "All" option
          if (hasAll) {
            return item.name.toLowerCase() !== "all";
          }

          // If we have selected values, apply more specific rules
          if (value?.length > 0) {
            if (getObject) {
              // Extract parts from the object name (prefix.suffix)
              const getObjectParts = getObject?.name?.split(regExp) || [];
              const itemParts = item.name.split(regExp);

              const getObjectSuffix = getObjectParts[1]?.toUpperCase();
              const itemSuffix = itemParts[1]?.toUpperCase();
              const getObjectPrefix = getObjectParts[0];
              const itemPrefix = itemParts[0];

              // If a selected item has an "ALL_*" suffix
              if (possibleAll.includes(getObjectSuffix)) {
                // Disable specific items with the same prefix
                // Example: If "schema.ALL_TABLES" is selected, disable "schema.specific_table"
                return (
                  !possibleAll.includes(itemSuffix) &&
                  itemPrefix === getObjectPrefix
                );
              } else {
                // If a specific item is selected, disable any "ALL_*" with the same prefix
                // Example: If "schema.specific_table" is selected, disable "schema.ALL_TABLES"
                return (
                  possibleAll.includes(itemSuffix) &&
                  getObjectPrefix === itemPrefix
                );
              }
            } else {
              // If we have values but no match on prefix, only disable the "All" option
              return item.name.toLowerCase() === "all";
            }
          }
          // Default: nothing is disabled
          return false;
        })(),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDeepCompareMemoize(data), value.length, hasAll]);

  const handleOnChange = (value: SelectData[]) => {
    setValue(value);
    input.onChange(value.map((item: any) => item.id));
    rest.onChange?.(value.map((item: any) => item.id));
  };

  useDeepCompareEffect(() => {
    if (value?.length > 0) {
      const newValues = compact(
        value.map(item =>
          data.find((dataItem: SelectData) => dataItem.id === item.id),
        ),
      );
      if (newValues.length && !isEqual(value, newValues))
        handleOnChange(newValues);
    }
  }, [data]);

  useDeepCompareEffect(() => {
    setDropDownData(newData);
  }, [newData]);

  const filterChange = (event: MultiSelectFilterChangeEvent) => {
    setDropDownData(filterBy([...newData], event.filter));
  };

  const isError = showErrorOnChange({ meta });
  const placeHolder = input.value?.length ? undefined : placeholder;
  return (
    <FinalFormWrapper input={input} meta={meta} {...rest}>
      <MultiSelect
        value={value}
        name={name}
        valid={!isError}
        disabled={disabled}
        loading={isLoading}
        placeholder={isLoading ? "Loading..." : placeHolder}
        filterable={true}
        textField="name"
        itemRender={FormSelectItemRender}
        onFilterChange={filterChange}
        onChange={({ value }) => handleOnChange(value)}
        dataItemKey="id"
        data={dropDownData}
        listNoDataRender={() => <ListNoData message={rest.noDataMessage} />}
      />
    </FinalFormWrapper>
  );
};

const SelectField = (props: SelectProps) => {
  const { name, ...rest } = props;
  return (
    <Field
      name={name}
      render={({ input, meta }) => {
        return (
          <SelectComponent name={name} input={input} meta={meta} {...rest} />
        );
      }}
      // {...rest} // removed this to avoid infinite rendering issue on shared object access policy principal selection
    />
  );
};

export const ComboBox = (props: ComboBoxProps) => {
  const { name, ...rest } = props;
  return (
    <Field
      name={name}
      render={({ input, meta }) => {
        return (
          <ComboBoxComponent
            name={name}
            input={input}
            meta={meta}
            {...rest}
            data={props.options}
          />
        );
      }}
      {...rest}
    />
  );
};
const MultiSelectField = (props: SelectProps) => {
  const { name, ...rest } = props;
  return (
    <Field
      name={name}
      render={({ input, meta }) => {
        return (
          <FormMultiSelect name={name} input={input} meta={meta} {...rest} />
        );
      }}
      {...rest}
    />
  );
};

export const Select = React.memo(SelectField);

export const Autocomplete = React.memo(
  (props: SelectProps & { multiple?: boolean; isLoading?: boolean }) => {
    const { multiple, ...rest } = props;
    if (multiple) {
      return <MultiSelectField {...rest} />;
    }
    return <SelectField {...rest} data={props.options} />;
  },
);

export const DatePickerField = (props: DatePickerProps) => {
  const { name, disabled, className, ...rest } = props;
  return (
    <Field
      name={name}
      render={({ input, meta }) => (
        <FinalFormWrapper
          input={input}
          meta={meta}
          disabled={disabled}
          className={className}
          {...rest}
        >
          <DatePicker
            disabled={disabled}
            {...input}
            {...rest}
            valid={!showErrorOnChange({ meta })}
            label=""
          />
        </FinalFormWrapper>
      )}
      {...rest}
    />
  );
};

export const PasswordField: React.FC<{
  name: string;
  label: string;
  placeholder: string;
}> = ({ name, label, placeholder }) => {
  const [show, setShow] = useState(false);
  const toogle = () => {
    setShow(!show);
  };

  return (
    <div className="password-wrapper" style={{ position: "relative" }}>
      <div
        style={{
          display: "inline-block",
          position: "absolute",
          right: "0.3125rem",
          top: "1.9375rem",
          zIndex: "999",
          color: "var(--tlx-text-primary)",
        }}
      >
        {show ? (
          <span
            className="k-icon k-font-icon k-i-eye-slash"
            style={{
              fontSize: "1rem",
              cursor: "pointer",
              display: "inline-block",
            }}
            onClick={toogle}
          />
        ) : (
          <span
            className="k-icon k-font-icon k-i-eye"
            style={{
              fontSize: "1rem",
              cursor: "pointer",
              display: "inline-block",
            }}
            onClick={toogle}
          />
        )}
      </div>
      <TextField
        name={name}
        label={label}
        placeholder={placeholder}
        required
        autoComplete="new-password"
        type={show ? "text" : "password"}
      />
    </div>
  );
};

/**
 * Converts a Yup schema to a react-final-form validator function
 * This is a replacement for mui-rff's makeValidate
 */
export function makeValidate<T = any>(
  // Deliberately `ISchema<unknown>` rather than `ObjectSchema<any>`: under the
  // TypeScript 7 checker the `any` degrades to an index signature inside yup's
  // `Shape<>` mapped type, so a concrete schema is reported as missing its own
  // named fields. Mirrors `yupResolver` in `@/components/Forms/yupResolver`.
  schema: Yup.ISchema<unknown>,
): (values: T) => Promise<ValidationErrors> {
  return async (values: T): Promise<ValidationErrors> => {
    try {
      await schema.validate(values, { abortEarly: false });
      return {};
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors: ValidationErrors = {};
        err.inner.forEach(error => {
          if (error.path) {
            errors[error.path] = error.message;
          }
        });
        return errors;
      }
      return {};
    }
  };
}
